import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useGameType } from '../../../contexts/GameTypeContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import api, { apiRoutes } from '../../../api';
import { getConfig } from '../../../config/environment';
import { getAllWinners } from '../../../data/drawsApi';
import { computeWinnerStats, mapWinnersFromApi } from '../../../utils/winnersApi';

const resolveDrawPrizeDescription = (draw, language) => {
  if (!draw) return null;
  const descriptions = draw.prizeDescriptions ?? draw.PrizeDescriptions ?? null;
  if (!descriptions) {
    return draw.prizeName ?? draw.PrizeName ?? draw.name ?? draw.Name ?? null;
  }
  if (typeof descriptions === 'string') return descriptions.trim() || null;
  if (typeof descriptions !== 'object') return null;
  return (
    descriptions[language] ||
    descriptions.en ||
    descriptions['en-CA'] ||
    Object.values(descriptions)[0] ||
    null
  );
};

const WinnersPageContext = createContext(null);

export function WinnersPageProvider({ children }) {
  const { gameType } = useGameType();
  const { language } = useLanguage();

  // Filter/UI state
  const [viewMode, setViewMode] = useState('grid');
  const [filterYear, setFilterYear] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Data state
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch winners data (and draw info for prize descriptions)
  useEffect(() => {
    const loadWinners = async () => {
      setLoading(true);

      try {
        const { tlfId } = getConfig();
        const [winnersRes, drawsRes] = await Promise.all([
          api.get(`${apiRoutes.SiteWinners}?organizationId=${tlfId}`),
          api.get(`${apiRoutes.OrganizationUpcomingDraws}?organizationId=${tlfId}`).catch(() => ({ data: null })),
        ]);

        const winnersPayload = winnersRes?.data?.payload || winnersRes?.data;
        const winnersData = winnersPayload?.data || winnersPayload?.Data || winnersPayload || [];

        const drawsPayload = drawsRes?.data?.payload || drawsRes?.data;
        const drawsData = drawsPayload?.data || drawsPayload?.Data || drawsPayload || [];
        const drawsById = Array.isArray(drawsData)
          ? Object.fromEntries(drawsData.map(d => [d.id ?? d.Id, d]))
          : {};

        const mapped = mapWinnersFromApi(winnersData, { language, gameType }).map((winner) => {
          if (winner.prize || gameType !== 'prizeDraw') return winner;
          const draw = drawsById[winner.drawId];
          const prizeDescription = resolveDrawPrizeDescription(draw, language);
          if (!prizeDescription) return winner;
          return { ...winner, prize: prizeDescription, isPrize: true };
        });

        setWinners(mapped);
      } catch (error) {
        console.error('Failed to load winners:', error);
        const fallback = getAllWinners().filter(w => w.gameType === gameType);
        setWinners(fallback);
      } finally {
        setLoading(false);
      }
    };

    loadWinners();
  }, [gameType, language]);

  // Computed stats
  const stats = useMemo(() => computeWinnerStats(winners), [winners]);

  // Available years and months derived from winners data
  const availableYears = useMemo(() => {
    const years = [...new Set(winners.map(w => w.year).filter(Boolean))];
    return years.sort((a, b) => b - a);
  }, [winners]);

  const availableMonths = useMemo(() => {
    if (filterYear === 'all') return [];
    const months = [...new Set(
      winners
        .filter(w => String(w.year) === filterYear && w.month)
        .map(w => w.month)
    )];
    return months.sort((a, b) => a - b);
  }, [winners, filterYear]);

  // Reset month filter when year changes
  useEffect(() => {
    setFilterMonth('all');
  }, [filterYear]);

  // Filtered winners based on search, year, and month
  const filteredWinners = useMemo(() => {
    return winners.filter(winner => {
      const matchesSearch = winner.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           winner.ticketNumber?.includes(searchTerm);
      const matchesYear = filterYear === 'all' || String(winner.year) === filterYear;
      const matchesMonth = filterMonth === 'all' || String(winner.month) === filterMonth;
      return matchesSearch && matchesYear && matchesMonth;
    });
  }, [winners, searchTerm, filterYear, filterMonth]);

  const refresh = async () => {
    setLoading(true);
    try {
      const { tlfId } = getConfig();
      const [winnersRes, drawsRes] = await Promise.all([
        api.get(`${apiRoutes.SiteWinners}?organizationId=${tlfId}`),
        api.get(`${apiRoutes.OrganizationUpcomingDraws}?organizationId=${tlfId}`).catch(() => ({ data: null })),
      ]);

      const winnersPayload = winnersRes?.data?.payload || winnersRes?.data;
      const winnersData = winnersPayload?.data || winnersPayload?.Data || winnersPayload || [];

      const drawsPayload = drawsRes?.data?.payload || drawsRes?.data;
      const drawsData = drawsPayload?.data || drawsPayload?.Data || drawsPayload || [];
      const drawsById = Array.isArray(drawsData)
        ? Object.fromEntries(drawsData.map(d => [d.id ?? d.Id, d]))
        : {};

      const mapped = mapWinnersFromApi(winnersData, { language, gameType }).map((winner) => {
        if (winner.prize || gameType !== 'prizeDraw') return winner;
        const draw = drawsById[winner.drawId];
        const prizeDescription = resolveDrawPrizeDescription(draw, language);
        if (!prizeDescription) return winner;
        return { ...winner, prize: prizeDescription, isPrize: true };
      });

      setWinners(mapped);
    } catch (error) {
      console.error('Failed to refresh winners:', error);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    // UI state
    viewMode,
    setViewMode,
    filterYear,
    setFilterYear,
    filterMonth,
    setFilterMonth,
    searchTerm,
    setSearchTerm,

    // Data
    winners,
    filteredWinners,
    stats,
    loading,
    availableYears,
    availableMonths,
    
    // Actions
    refresh,
    gameType,
  };

  return (
    <WinnersPageContext.Provider value={value}>
      {children}
    </WinnersPageContext.Provider>
  );
}

export function useWinnersPage() {
  const context = useContext(WinnersPageContext);
  if (!context) {
    throw new Error('useWinnersPage must be used within WinnersPageProvider');
  }
  return context;
}
